import { api, type ApiError, type ComponentItem } from '../api/client.js';

const CONFIRM_PHRASE = 'delete me';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Opens the "Delete custom component" confirmation modal.
 *
 * The Delete button stays disabled until the user types the literal
 * phrase `delete me` (case-insensitive, leading/trailing whitespace
 * trimmed) into the confirmation input. Submitting calls
 * `DELETE /api/components/:id`; the backend hard-deletes the DB row
 * and best-effort wipes the S3 prefix.
 *
 * Resolves with `true` after a successful delete (so the caller can
 * refresh the list), `false` on dismiss / cancel.
 */
export function openDeleteComponentModal(
  component: Pick<ComponentItem, 'id' | 'name'>,
): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = document.createElement('sl-dialog');
    dialog.label = 'Delete custom component';
    dialog.innerHTML = `
      <div class="field-stack">
        <sl-alert variant="warning" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          <strong>This cannot be undone.</strong>
          The component <code>${escapeHtml(component.name)}</code> and every
          published version (including all bundle files in object storage)
          will be permanently removed.
        </sl-alert>

        <sl-input
          id="dc-confirm"
          autocomplete="off"
          spellcheck="false"
          required>
          <span slot="label">Type '<strong>${escapeHtml(CONFIRM_PHRASE)}</strong>' to confirm</span>
        </sl-input>

        <sl-alert id="dc-error" variant="danger" closable></sl-alert>
      </div>

      <sl-button slot="footer" id="dc-cancel" variant="default">Cancel</sl-button>
      <sl-button slot="footer" id="dc-delete" variant="danger" disabled>Delete</sl-button>
    `;

    document.body.appendChild(dialog);

    const confirmEl = dialog.querySelector<HTMLInputElement & { value: string }>('#dc-confirm')!;
    const errorEl = dialog.querySelector<HTMLElement & { open: boolean }>('#dc-error')!;
    const cancelBtn = dialog.querySelector<HTMLElement>('#dc-cancel')!;
    const deleteBtn = dialog.querySelector<HTMLElement & { disabled: boolean; loading?: boolean }>(
      '#dc-delete',
    )!;

    let succeeded = false;

    const showError = (msg: string) => {
      errorEl.textContent = msg;
      errorEl.open = true;
    };
    const hideError = () => {
      errorEl.open = false;
    };

    const isConfirmed = (): boolean =>
      confirmEl.value.trim().toLowerCase() === CONFIRM_PHRASE;

    confirmEl.addEventListener('sl-input', () => {
      deleteBtn.disabled = !isConfirmed();
      hideError();
    });

    cancelBtn.addEventListener('click', () => dialog.hide());

    dialog.addEventListener('sl-after-hide', () => {
      dialog.remove();
      resolve(succeeded);
    });

    deleteBtn.addEventListener('click', async () => {
      if (!isConfirmed()) {
        showError(`Type "${CONFIRM_PHRASE}" exactly to confirm`);
        return;
      }
      hideError();
      deleteBtn.loading = true;
      cancelBtn.setAttribute('disabled', '');
      try {
        await api.deleteComponent(component.id);
        succeeded = true;
        dialog.hide();
      } catch (err) {
        const apiErr = err as ApiError;
        showError(apiErr?.message ?? 'Failed to delete component');
        deleteBtn.loading = false;
        cancelBtn.removeAttribute('disabled');
      }
    });

    requestAnimationFrame(() => {
      (dialog as HTMLElement & { show: () => void }).show();
    });
  });
}
